import { LayoutDashboard, MessagesSquare, TrendingUp, Fingerprint, MessageCircle } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { FadeUp, PageHero, Eyebrow } from "@/components/Reveal";

const AREAS = [
  {
    icon: <LayoutDashboard size={20} />,
    title: "Gestione account & strategia",
    text: "Calendario editoriale, pricing, promozioni e posizionamento: ogni decisione segue una strategia condivisa e misurabile.",
    testid: "creator-area-account",
  },
  {
    icon: <MessagesSquare size={20} />,
    title: "Chat & Sales 24/7",
    text: "Un team di vendita professionale gestisce le conversazioni con script testati e upselling strutturato, giorno e notte.",
    testid: "creator-area-sales",
  },
  {
    icon: <TrendingUp size={20} />,
    title: "Crescita social & traffico",
    text: "Piani di crescita multi-piattaforma e acquisizione traffico mirata per alimentare il tuo funnel in modo costante.",
    testid: "creator-area-growth",
  },
  {
    icon: <Fingerprint size={20} />,
    title: "Identità Iconica & Posizionamento d'Élite",
    text: "Costruiamo un'identità visiva e strategica su misura per te: trasformiamo la tua immagine in un brand d'élite, riconoscibile, esclusivo e desiderabile.",
    testid: "creator-area-brand",
  },
];

const STEPS = [
  { n: "01", title: "Candidatura", text: "Scrivici su WhatsApp. Ogni candidatura è riservata e valutata individualmente." },
  { n: "02", title: "Call conoscitiva", text: "Analizziamo obiettivi, potenziale e aspettative. Nessun impegno, massima trasparenza." },
  { n: "03", title: "Onboarding", text: "Definiamo strategia, processi e protezioni. Da lì, il sistema lavora per te." },
];

export default function Creator() {
  const { openContact } = useSite();

  return (
    <main data-testid="creator-page">
      <PageHero
        testid="creator-hero"
        eyebrow="Per le Creator"
        lines={[
          "La tua immagine.",
          <span key="l2" className="gold-text gold-text-animated italic">Il nostro sistema.</span>,
        ]}
        sub="Mettiamo a tua disposizione una struttura manageriale completa per trasformare la tua presenza online in un brand iconico, liberando il tuo tempo e portando i tuoi guadagni al massimo livello."
      />

      <section className="py-14 lg:py-20" data-testid="creator-areas">
        <div className="mx-auto max-w-7xl px-6">
          <FadeUp>
            <Eyebrow>Cosa gestiamo per te</Eyebrow>
          </FadeUp>
          <div className="mt-12 grid gap-px border border-[#1A1A1A] bg-[#1A1A1A] sm:grid-cols-2">
            {AREAS.map((a, i) => (
              <FadeUp key={a.title} delay={i * 0.07} className="group bg-[#0b0b0b] p-8 lg:p-12 transition-colors duration-500 hover:bg-[#111111]">
                <div data-testid={a.testid}>
                  <div className="flex h-11 w-11 items-center justify-center border border-gold/25 text-gold transition-colors duration-500 group-hover:border-gold/60">
                    {a.icon}
                  </div>
                  <h3 className="mt-6 font-serif text-xl sm:text-2xl text-[#F5F3EE]">{a.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#969696]">{a.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-[#1A1A1A] py-14 lg:py-20" data-testid="creator-ecosystems">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-2">
          <FadeUp>
            <Eyebrow>Ecosistemi di monetizzazione</Eyebrow>
            <h2 className="mt-5 font-serif text-2xl sm:text-3xl lg:text-4xl leading-tight text-[#F5F3EE]">
              OnlyFans è l'ecosistema. <span className="italic text-gold">Il brand sei tu.</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.15}>
            <p className="text-sm sm:text-base leading-relaxed text-[#969696]">
              OnlyFans è uno dei principali ecosistemi di monetizzazione in cui IEM — Iconic Elite Management
              S.R.L.S. opera, con processi strutturati, team di vendita dedicati e standard professionali. Ma una
              piattaforma è solo uno strumento: il nostro lavoro è trasformare la tua audience in un'azienda
              digitale scalabile, capace di crescere su ogni canale.
            </p>
            <p className="mt-5 text-sm sm:text-base leading-relaxed text-[#969696]">
              Social, membership, collaborazioni e iniziative proprietarie: costruiamo un ecosistema di ricavi che
              non dipende da un'unica fonte.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="border-t border-[#1A1A1A] py-14 lg:py-20" data-testid="creator-steps">
        <div className="mx-auto max-w-7xl px-6">
          <FadeUp>
            <Eyebrow>Come iniziare</Eyebrow>
          </FadeUp>
          <div className="mt-12 grid gap-10 lg:grid-cols-3">
            {STEPS.map((s, i) => (
              <FadeUp key={s.n} delay={i * 0.1} className="border-t border-gold/30 pt-6">
                <div data-testid={`creator-step-${s.n}`}>
                  <span className="font-serif text-3xl text-gold/80">{s.n}</span>
                  <h3 className="mt-4 font-serif text-xl sm:text-2xl text-[#F5F3EE]">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#969696]">{s.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.3} className="mt-14">
            <button
              data-testid="creator-page-cta"
              onClick={() => openContact("creator")}
              className="inline-flex items-center gap-3 bg-gold px-8 py-4 text-[13px] font-bold uppercase tracking-[0.18em] text-[#080808] transition-all duration-300 hover:bg-gold-bright hover:shadow-[0_0_30px_rgba(229,196,90,0.25)]"
            >
              <MessageCircle size={16} /> Candidati come Creator
            </button>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
